import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Download, LogOut, Search, ArrowLeft, ExternalLink, FileText, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { adminApi, authedFetch } from "@/lib/apiClient";
import { fmtINR } from "@/lib/financial";

const STATUS_OPTIONS = [
  { value: "new", label: "New", cls: "bg-[#A7C7E7]/25 text-[#1A3D6B]" },
  { value: "in_review", label: "In Review", cls: "bg-[#C78D4A]/15 text-[#C97B2A]" },
  { value: "plan_ready", label: "Plan Ready", cls: "bg-[#3A5C45]/15 text-[#3A5C45]" },
  { value: "active", label: "Active", cls: "bg-[#1A3626] text-white" },
  { value: "on_hold", label: "On Hold", cls: "bg-[#9E473D]/15 text-[#9E473D]" },
  { value: "archived", label: "Archived", cls: "bg-[#8A8D8A]/20 text-[#5B5E5B]" },
];
const statusMeta = (v) => STATUS_OPTIONS.find((s) => s.value === v) || STATUS_OPTIONS[0];

function useAdminGuard() {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    let mounted = true;
    adminApi.get("/admin/me")
      .then(() => { if (mounted) setChecked(true); })
      .catch((err) => {
        if (mounted) {
          console.error("admin guard failed", err);
          navigate("/admin/login");
        }
      });
    return () => { mounted = false; };
  }, [navigate]);
  return checked;
}

function AdminHeader() {
  const navigate = useNavigate();
  const logout = async () => {
    try {
      await adminApi.post("/admin/logout");
    } catch (err) {
      console.error("admin logout failed", err);
    }
    navigate("/admin/login");
  };
  return (
    <header className="border-b border-[#E5E2DA] bg-white/80 backdrop-blur-xl sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between py-4">
        <Link to="/admin" className="inline-flex items-center gap-3">
          <img src="/wah-logo.png" alt="Wealth Accelerator Hub" className="h-10 w-auto"/>
          <span className="text-sm text-[#8B9D8B] font-medium border-l border-[#E5E2DA] pl-3">Advisor</span>
        </Link>
        <Button data-testid="admin-logout" variant="ghost" onClick={logout} className="rounded-full">
          <LogOut className="h-4 w-4 mr-2"/> Sign out
        </Button>
      </div>
    </header>
  );
}

export function AdminDashboard() {
  useAdminGuard();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/clients");
        if (mounted) setClients(data);
      } catch (err) {
        console.error("admin clients fetch failed", err);
        if (err.response?.status === 401) navigate("/admin/login");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [navigate]);

  const filtered = useMemo(() => {
    if (!q) return clients;
    const needle = q.toLowerCase();
    return clients.filter((c) => c.name.toLowerCase().includes(needle) || c.email.toLowerCase().includes(needle));
  }, [clients, q]);

  const { totalSubmitted, totalNetWorth } = useMemo(() => ({
    totalSubmitted: clients.filter((c) => c.submitted).length,
    totalNetWorth: clients.reduce((s, c) => s + (c.net_worth || 0), 0),
  }), [clients]);

  const exportCsv = async () => {
    try {
      const res = await authedFetch("/admin/clients/export");
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wealth-accelerator-clients-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err) {
      console.error("csv export failed", err);
      toast.error("Couldn't export");
    }
  };

  return (
    <div className="min-h-screen page-tinted-soft">
      <AdminHeader/>
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Practice overview</span>
            <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight mt-3">Clients</h1>
            <p className="text-[#5B5E5B] mt-2">All client submissions, exportable to your sheet anytime.</p>
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A8D8A]"/>
              <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email"
                className="pl-10 h-11 w-72 rounded-full border-[#E5E2DA] bg-white focus-visible:ring-[#1A3626]/20 focus-visible:ring-2"/>
            </div>
            <Button data-testid="admin-export" onClick={exportCsv} className="rounded-full bg-[#1A3626] hover:bg-[#2C4C3B] text-white">
              <Download className="h-4 w-4 mr-2"/> Export CSV
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Clients</div>
            <div className="mono text-3xl mt-3">{clients.length}</div>
          </div>
          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Submitted</div>
            <div className="mono text-3xl mt-3">{totalSubmitted}</div>
          </div>
          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Combined net worth</div>
            <div className="mono text-3xl mt-3">₹ {fmtINR(totalNetWorth)}</div>
          </div>
        </div>

        <div className="rounded-2xl border border-[#E5E2DA] bg-white overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-[#8A8D8A]">Loading clients…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-[#8A8D8A]">No clients yet — share the landing page with them to begin.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E5E2DA] hover:bg-transparent">
                  <TableHead className="text-[#8B9D8B] uppercase text-xs tracking-[0.15em] font-bold">Client</TableHead>
                  <TableHead className="text-[#8B9D8B] uppercase text-xs tracking-[0.15em] font-bold">Status</TableHead>
                  <TableHead className="text-[#8B9D8B] uppercase text-xs tracking-[0.15em] font-bold">Submission</TableHead>
                  <TableHead className="text-[#8B9D8B] uppercase text-xs tracking-[0.15em] font-bold text-right">Monthly</TableHead>
                  <TableHead className="text-[#8B9D8B] uppercase text-xs tracking-[0.15em] font-bold text-right">Net worth</TableHead>
                  <TableHead className="text-[#8B9D8B] uppercase text-xs tracking-[0.15em] font-bold text-right">EMI %</TableHead>
                  <TableHead className="text-[#8B9D8B] uppercase text-xs tracking-[0.15em] font-bold text-right">SIP</TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow key={c.user_id} data-testid={`client-row-${c.user_id}`}
                    onClick={() => navigate(`/admin/clients/${c.user_id}`)}
                    className="border-[#E5E2DA] cursor-pointer hover:bg-[#F4F2EC]/60 transition-colors">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {c.picture ? (
                          <img src={c.picture} alt="" className="h-9 w-9 rounded-full object-cover border border-[#E5E2DA]"/>
                        ) : (
                          <div className="h-9 w-9 rounded-full bg-[#F4F2EC] flex items-center justify-center text-sm font-medium text-[#1A3626]">
                            {(c.name || "?").charAt(0)}
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-[#1C1D1C]">{c.name}</div>
                          <div className="text-sm text-[#8A8D8A]">{c.email}</div>
                          {(c.tags || []).length > 0 && (
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {(c.tags || []).slice(0, 3).map((t, i) => (
                                <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-[#F4F2EC] text-[#5B5E5B]">{t}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${statusMeta(c.status).cls} border-0 rounded-full hover:opacity-90`}>{statusMeta(c.status).label}</Badge>
                    </TableCell>
                    <TableCell>
                      {c.submitted
                        ? <Badge className="bg-[#3A5C45]/10 text-[#3A5C45] hover:bg-[#3A5C45]/15 border-0 rounded-full">Submitted</Badge>
                        : <Badge className="bg-[#C78D4A]/10 text-[#C78D4A] hover:bg-[#C78D4A]/15 border-0 rounded-full">Draft</Badge>}
                    </TableCell>
                    <TableCell className="text-right mono">₹ {fmtINR(c.monthly_inflow)}</TableCell>
                    <TableCell className={`text-right mono ${(c.net_worth || 0) < 0 ? "text-[#9E473D]" : ""}`}>₹ {fmtINR(c.net_worth)}</TableCell>
                    <TableCell className="text-right mono">{(c.emi_ratio || 0).toFixed(1)}%</TableCell>
                    <TableCell className="text-right mono">₹ {fmtINR(c.monthly_sip)}</TableCell>
                    <TableCell className="text-right"><ExternalLink className="h-4 w-4 text-[#8A8D8A] inline"/></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="py-2 flex items-baseline justify-between gap-6 border-b border-[#E5E2DA]/60 last:border-0">
      <div className="text-sm text-[#8A8D8A]">{label}</div>
      <div className="mono text-[#1C1D1C] text-right">{value}</div>
    </div>
  );
}

const num = (x) => Number(x || 0);

export function AdminClientDetail() {
  useAdminGuard();
  const { user_id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ status: "new", advisor_notes: "", tags: [] });
  const [tagsInput, setTagsInput] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);
  const [networth, setNetworth] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await adminApi.get(`/admin/clients/${user_id}`);
        if (!mounted) return;
        setData(res.data);
        const m = res.data.meta || { status: "new", advisor_notes: "", tags: [] };
        setMeta(m);
        setTagsInput((m.tags || []).join(", "));
        try {
          const nw = await adminApi.get(`/admin/clients/${user_id}/net-worth`);
          if (mounted) setNetworth(nw.data || []);
        } catch (e) { console.error("net-worth fetch failed", e); }
      } catch (err) {
        console.error("client detail fetch failed", err);
        if (err.response?.status === 401) navigate("/admin/login");
        if (err.response?.status === 404) toast.error("Client not found");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [user_id, navigate]);

  const saveMeta = useCallback(async () => {
    setSavingMeta(true);
    try {
      const tags = tagsInput.split(",").map((t) => t.trim()).filter(Boolean);
      const { data: m } = await adminApi.patch(`/admin/clients/${user_id}/meta`, {
        status: meta.status, advisor_notes: meta.advisor_notes, tags,
      });
      setMeta(m);
      setTagsInput((m.tags || []).join(", "));
      toast.success("Saved");
    } catch (err) {
      console.error("save meta failed", err);
      toast.error("Couldn't save");
    } finally {
      setSavingMeta(false);
    }
  }, [user_id, meta.status, meta.advisor_notes, tagsInput]);

  const downloadPdf = useCallback(async () => {
    try {
      const res = await authedFetch(`/admin/clients/${user_id}/snapshot.pdf`);
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const fname = (data?.profile?.personal?.full_name || data?.user?.name || "client").replace(/ /g, "_");
      a.href = url;
      a.download = `WAH_Snapshot_${fname}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Snapshot downloaded");
    } catch (err) {
      console.error("admin pdf download failed", err);
      toast.error("Couldn't download PDF");
    }
  }, [user_id, data]);

  if (loading) return <div className="min-h-screen page-tinted-soft"><AdminHeader/><div className="p-12 text-center text-[#5B5E5B]">Loading…</div></div>;
  if (!data) return null;
  const { user, profile, summary } = data;
  const p = profile;
  const fa = p.financial_assets || {}, fx = p.fixed_assets || {};

  return (
    <div className="min-h-screen page-tinted-soft">
      <AdminHeader/>
      <main className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <button data-testid="back-to-list" onClick={() => navigate("/admin")} className="text-sm text-[#5B5E5B] hover:text-[#1C1D1C] inline-flex items-center mb-6">
          <ArrowLeft className="h-4 w-4 mr-1"/> All clients
        </button>

        <div className="flex items-start justify-between gap-6 mb-10 flex-wrap">
          <div className="flex items-center gap-4">
            {user.picture ? (
              <img src={user.picture} alt="" className="h-16 w-16 rounded-full object-cover border border-[#E5E2DA]"/>
            ) : (
              <div className="h-16 w-16 rounded-full bg-[#F4F2EC] flex items-center justify-center text-2xl font-heading text-[#1A3626]">
                {(user.name || "?").charAt(0)}
              </div>
            )}
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-medium">{p.personal?.full_name || user.name}</h1>
              <div className="text-[#5B5E5B] mt-1">{user.email} · {p.personal?.phone || "—"}</div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge className={`${statusMeta(meta.status).cls} border-0 rounded-full`}>{statusMeta(meta.status).label}</Badge>
                {(meta.tags || []).map((t, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-[#F4F2EC] text-[#5B5E5B]">{t}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge className={`rounded-full border-0 ${p.submitted ? "bg-[#3A5C45]/10 text-[#3A5C45]" : "bg-[#C78D4A]/10 text-[#C78D4A]"}`}>
              {p.submitted ? "Submitted" : "Draft"}
            </Badge>
            <Button data-testid="admin-download-pdf" onClick={downloadPdf} variant="outline" className="rounded-full border-[#E5E2DA]">
              <FileText className="h-4 w-4 mr-2"/> Download PDF
            </Button>
          </div>
        </div>

        {/* 4-quadrant financial worksheet */}
        <div className="rounded-2xl border border-[#E5E2DA] bg-white overflow-hidden mb-8" data-testid="quadrant-worksheet">
          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#E5E2DA]">
            {/* Top-left: Income */}
            <div className="p-7 bg-[#3A5C45]/[0.04]">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#3A5C45] mb-2">Income · Monthly</div>
              <div className="mono text-2xl font-medium mb-4">₹ {fmtINR(summary.monthly_inflow)}</div>
              <div className="space-y-1.5 text-sm">
                {[["Salary (Self)", p.income?.salary_self], ["Salary (Spouse)", p.income?.salary_spouse],
                  ["Business", p.income?.business_income], ["Rent", p.income?.rent_received],
                  ["Interest", p.income?.interest_received], ["Dividend", p.income?.dividend_received],
                  ["Other", p.income?.other_income]].filter(([_, v]) => num(v) > 0).map(([l, v]) => (
                  <div key={l} className="flex justify-between border-b border-[#E5E2DA]/60 py-1.5">
                    <span className="text-[#5B5E5B]">{l}</span>
                    <span className="mono">₹ {fmtINR(v)}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-2 mt-2 border-t border-[#3A5C45]/20 text-[#3A5C45] font-semibold">
                  <span>Annual extras</span>
                  <span className="mono">₹ {fmtINR(summary.yearly_inflow)}</span>
                </div>
              </div>
            </div>
            {/* Top-right: Expenses */}
            <div className="p-7 bg-[#C78D4A]/[0.05]">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A] mb-2">Expenses · Monthly</div>
              <div className="mono text-2xl font-medium mb-4">₹ {fmtINR(summary.monthly_expense)}</div>
              <div className="space-y-1.5 text-sm">
                {Object.entries(p.expenses || {}).map(([group, items]) => {
                  const total = (items || []).reduce((s, it) => {
                    const a = num(it.amount); return s + (it.frequency === "Annually" ? a/12 : a);
                  }, 0);
                  if (total <= 0) return null;
                  return (
                    <div key={group} className="flex justify-between border-b border-[#E5E2DA]/60 py-1.5">
                      <span className="text-[#5B5E5B] capitalize">{group.replace(/_/g, " & ")}</span>
                      <span className="mono">₹ {fmtINR(total)}</span>
                    </div>
                  );
                })}
                <div className="flex justify-between pt-2 mt-2 border-t border-[#C97B2A]/20 text-[#C97B2A] font-semibold">
                  <span>Annual total</span>
                  <span className="mono">₹ {fmtINR(summary.annual_expense)}</span>
                </div>
              </div>
            </div>
            {/* Bottom-left: Assets */}
            <div className="p-7 bg-[#1A3626]/[0.04]">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A3626] mb-2">Assets</div>
              <div className="mono text-2xl font-medium mb-4">₹ {fmtINR(summary.total_assets)}</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between border-b border-[#E5E2DA]/60 py-1.5">
                  <span className="text-[#5B5E5B]">Financial assets</span>
                  <span className="mono">₹ {fmtINR(summary.total_financial_assets)}</span>
                </div>
                <div className="flex justify-between border-b border-[#E5E2DA]/60 py-1.5">
                  <span className="text-[#5B5E5B]">Fixed / lifestyle assets</span>
                  <span className="mono">₹ {fmtINR(summary.total_fixed_assets)}</span>
                </div>
                {Object.entries(fa).filter(([_, v]) => num(v) > 0).slice(0, 6).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-[#E5E2DA]/60 py-1.5">
                    <span className="text-[#5B5E5B] capitalize text-xs">{k.replace(/_/g, " ")}</span>
                    <span className="mono text-xs">₹ {fmtINR(v)}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-2 mt-2 border-t border-[#1A3626]/20 text-[#1A3626] font-semibold">
                  <span>Monthly SIP</span>
                  <span className="mono">₹ {fmtINR(summary.monthly_sip)}</span>
                </div>
              </div>
            </div>
            {/* Bottom-right: Liabilities */}
            <div className="p-7 bg-[#9E473D]/[0.04]">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#9E473D] mb-2">Liabilities</div>
              <div className="mono text-2xl font-medium mb-4">₹ {fmtINR(summary.total_liabilities)}</div>
              <div className="space-y-1.5 text-sm">
                {(p.liabilities_table || []).map((l, i) => (
                  <div key={l._id || `l-${i}`} className="flex justify-between border-b border-[#E5E2DA]/60 py-1.5">
                    <span className="text-[#5B5E5B]">{l.type}{l.lender ? ` — ${l.lender}` : ""}</span>
                    <span className="mono">₹ {fmtINR(l.outstanding)}</span>
                  </div>
                ))}
                {(p.liabilities_table || []).length === 0 && <div className="text-sm text-[#8A8D8A]">No loans recorded.</div>}
                <div className="flex justify-between pt-2 mt-2 border-t border-[#9E473D]/20 text-[#9E473D] font-semibold">
                  <span>Monthly EMI</span>
                  <span className="mono">₹ {fmtINR(summary.total_emi)} ({(summary.emi_ratio || 0).toFixed(1)}%)</span>
                </div>
              </div>
            </div>
          </div>
          <div className="px-7 py-4 bg-[#FDFBF7] border-t border-[#E5E2DA] flex items-center justify-between">
            <div className="text-sm text-[#5B5E5B]">Net worth (Assets − Liabilities)</div>
            <div className={`mono text-xl font-medium ${summary.net_worth >= 0 ? "text-[#1A3626]" : "text-[#9E473D]"}`}>₹ {fmtINR(summary.net_worth)}</div>
          </div>
        </div>

        {/* Net Worth tracker entries */}
        {networth.length > 0 && (
          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-7 mb-8" data-testid="admin-networth-section">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Net Worth Tracker</div>
                <div className="font-heading text-xl mt-1">{networth.length} monthly {networth.length === 1 ? "entry" : "entries"}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-[#8A8D8A]">Latest net worth</div>
                <div className="mono text-2xl text-[#1A3626] font-medium">₹ {fmtINR(networth[networth.length - 1]?.net_worth || 0)}</div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#8B9D8B] uppercase text-xs tracking-[0.15em]">
                    <th className="py-2 font-bold">Month</th>
                    <th className="py-2 font-bold text-right">Assets</th>
                    <th className="py-2 font-bold text-right">Liabilities</th>
                    <th className="py-2 font-bold text-right">Net worth</th>
                    <th className="py-2 font-bold text-right">Δ vs prev</th>
                  </tr>
                </thead>
                <tbody>
                  {[...networth].reverse().map((e, idx, arr) => {
                    const prev = arr[idx + 1];
                    const delta = prev ? e.net_worth - prev.net_worth : null;
                    const dCls = delta == null ? "text-[#8A8D8A]" : delta > 0 ? "text-[#3A5C45]" : delta < 0 ? "text-[#9E473D]" : "text-[#8A8D8A]";
                    return (
                      <tr key={e.entry_date} className="border-t border-[#E5E2DA]/60">
                        <td className="py-3 font-medium">{new Date(e.entry_date).toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</td>
                        <td className="py-3 text-right mono">₹ {fmtINR(e.total_assets)}</td>
                        <td className="py-3 text-right mono text-[#9E473D]">₹ {fmtINR(e.total_liabilities)}</td>
                        <td className="py-3 text-right mono text-[#1A3626] font-medium">₹ {fmtINR(e.net_worth)}</td>
                        <td className={`py-3 text-right mono ${dCls}`}>{delta == null ? "—" : `${delta >= 0 ? "+" : "−"}₹ ${fmtINR(Math.abs(delta))}`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Advisor notes & status */}
        <div className="rounded-2xl border border-[#E5E2DA] bg-white p-7 mb-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Advisor workspace</div>
              <div className="font-heading text-xl mt-1">Status, tags & private notes</div>
              <p className="text-sm text-[#8A8D8A] mt-1">Only you see this. Use it to track where each client stands.</p>
            </div>
            <Button data-testid="save-meta" onClick={saveMeta} disabled={savingMeta} className="rounded-full bg-[#1A3626] hover:bg-[#2C4C3B] text-white">
              <Save className="h-4 w-4 mr-2"/> {savingMeta ? "Saving…" : "Save"}
            </Button>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-[#1C1D1C] mb-2">Status</label>
              <Select value={meta.status || "new"} onValueChange={(v) => setMeta((m) => ({ ...m, status: v }))}>
                <SelectTrigger data-testid="meta-status" className="h-11 rounded-xl border-[#E5E2DA] bg-white"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1C1D1C] mb-2">Tags (comma-separated)</label>
              <Input data-testid="meta-tags" value={tagsInput} onChange={(e) => setTagsInput(e.target.value)}
                placeholder="vip, retired, salaried, follow-up"
                className="h-11 rounded-xl border-[#E5E2DA] bg-white focus-visible:ring-[#1A3626]/20 focus-visible:ring-2"/>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#1C1D1C] mb-2">Private notes</label>
              <Textarea data-testid="meta-notes" value={meta.advisor_notes || ""}
                onChange={(e) => setMeta((m) => ({ ...m, advisor_notes: e.target.value }))}
                placeholder="Plan ideas, action items, follow-ups…"
                className="min-h-[120px] rounded-xl border-[#E5E2DA] bg-white"/>
              {meta.updated_at && <div className="text-xs text-[#8A8D8A] mt-2">Last updated {new Date(meta.updated_at).toLocaleString()}</div>}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {[
            ["Monthly inflow", `₹ ${fmtINR(summary.monthly_inflow)}`],
            ["Monthly expense", `₹ ${fmtINR(summary.monthly_expense)}`],
            ["Net worth", `₹ ${fmtINR(summary.net_worth)}`],
            ["EMI / income", `${(summary.emi_ratio || 0).toFixed(1)}%`],
          ].map(([l, v], i) => (
            <div key={i} className="rounded-2xl border border-[#E5E2DA] bg-white p-6">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">{l}</div>
              <div className="mono text-2xl mt-3">{v}</div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-7">
            <div className="font-heading text-xl mb-4">Personal</div>
            <Field label="Full name" value={p.personal?.full_name || "—"}/>
            <Field label="Phone" value={p.personal?.phone || "—"}/>
            <Field label="Age" value={p.personal?.age ?? "—"}/>
            <Field label="Occupation" value={p.personal?.occupation || "—"}/>
            <Field label="City" value={p.personal?.city || "—"}/>
            <Field label="Marital status" value={p.personal?.marital_status || "—"}/>
            <Field label="Year of planning" value={p.personal?.year_of_planning || "—"}/>
          </div>

          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-7">
            <div className="font-heading text-xl mb-4">Family</div>
            <Field label="Spouse name" value={p.family?.spouse?.name || "—"}/>
            <Field label="Spouse age" value={p.family?.spouse?.age || "—"}/>
            <Field label="Spouse occupation" value={p.family?.spouse?.occupation || "—"}/>
            <Field label="Children" value={(p.family?.children || []).length}/>
            {(p.family?.children || []).map((c, i) => (
              <Field key={c._id || c.name || `child-detail-${i}`} label={`Child ${i+1}`} value={`${c.name || "—"} (${c.age || "—"})`}/>
            ))}
          </div>

          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-7">
            <div className="font-heading text-xl mb-4">Retirement plan</div>
            <Field label="Current age" value={p.retirement?.current_age || "—"}/>
            <Field label="Retirement age" value={p.retirement?.retirement_age || "—"}/>
            <Field label="Retirement year" value={p.retirement?.retirement_year || "—"}/>
            <Field label="Life expectancy" value={p.retirement?.life_expectancy || "—"}/>
            <Field label="Post-retirement income" value={`₹ ${fmtINR(p.retirement?.post_retirement_income)}`}/>
            <Field label="Monthly expense today" value={`₹ ${fmtINR(p.retirement?.monthly_expense_today)}`}/>
          </div>

          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-7">
            <div className="font-heading text-xl mb-4">Income</div>
            <Field label="Salary (Self)" value={`₹ ${fmtINR(p.income?.salary_self)}`}/>
            <Field label="PF (Self)" value={`₹ ${fmtINR(p.income?.pf_self)}`}/>
            <Field label="Salary (Spouse)" value={`₹ ${fmtINR(p.income?.salary_spouse)}`}/>
            <Field label="PF (Spouse)" value={`₹ ${fmtINR(p.income?.pf_spouse)}`}/>
            <Field label="Business income" value={`₹ ${fmtINR(p.income?.business_income)}`}/>
            <Field label="Rent received" value={`₹ ${fmtINR(p.income?.rent_received)}`}/>
            <Field label="Interest" value={`₹ ${fmtINR(p.income?.interest_received)}`}/>
            <Field label="Dividend" value={`₹ ${fmtINR(p.income?.dividend_received)}`}/>
            <Field label="Other monthly" value={`₹ ${fmtINR(p.income?.other_income)}`}/>
            <Field label="Bonus (Self/yr)" value={`₹ ${fmtINR(p.income?.bonus_self)}`}/>
            <Field label="Bonus (Spouse/yr)" value={`₹ ${fmtINR(p.income?.bonus_spouse)}`}/>
            <Field label="Business yearly" value={`₹ ${fmtINR(p.income?.business_yearly)}`}/>
          </div>

          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-7">
            <div className="font-heading text-xl mb-4">Financial assets</div>
            {Object.entries(fa).filter(([_, v]) => num(v) > 0).map(([k, v]) => (
              <Field key={k} label={k.replace(/_/g, " ")} value={`₹ ${fmtINR(v)}`}/>
            ))}
            {Object.values(fa).every((v) => !num(v)) && <div className="text-sm text-[#8A8D8A]">None recorded.</div>}
            <Field label="Total" value={`₹ ${fmtINR(summary.total_financial_assets)}`}/>
          </div>

          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-7">
            <div className="font-heading text-xl mb-4">Fixed assets</div>
            {Object.entries(fx).filter(([_, v]) => num(v) > 0).map(([k, v]) => (
              <Field key={k} label={k.replace(/_/g, " ")} value={`₹ ${fmtINR(v)}`}/>
            ))}
            {Object.values(fx).every((v) => !num(v)) && <div className="text-sm text-[#8A8D8A]">None recorded.</div>}
            <Field label="Total" value={`₹ ${fmtINR(summary.total_fixed_assets)}`}/>
          </div>

          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-7 md:col-span-2">
            <div className="font-heading text-xl mb-4">Loans & EMIs</div>
            {(p.liabilities_table || []).length === 0 && <div className="text-sm text-[#8A8D8A]">No loans recorded.</div>}
            <div className="space-y-3">
              {(p.liabilities_table || []).map((l, i) => (
                <div key={i} className="rounded-xl border border-[#E5E2DA] p-4 grid sm:grid-cols-7 gap-3 text-sm">
                  <div><div className="text-xs text-[#8A8D8A]">Type</div><div className="font-medium">{l.type}</div></div>
                  <div><div className="text-xs text-[#8A8D8A]">Lender</div><div>{l.lender || "—"}</div></div>
                  <div><div className="text-xs text-[#8A8D8A]">Start</div><div className="mono">{l.start_year || "—"}</div></div>
                  <div><div className="text-xs text-[#8A8D8A]">End</div><div className="mono">{l.end_year || "—"}</div></div>
                  <div><div className="text-xs text-[#8A8D8A]">EMI</div><div className="mono">₹ {fmtINR(l.monthly_emi)}</div></div>
                  <div><div className="text-xs text-[#8A8D8A]">Rate</div><div className="mono">{l.interest_rate || "—"}%</div></div>
                  <div><div className="text-xs text-[#8A8D8A]">Outstanding</div><div className="mono">₹ {fmtINR(l.outstanding)}</div></div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-7">
            <div className="font-heading text-xl mb-4">Investments</div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A] mb-2">Monthly</div>
            {Object.entries(p.investments_monthly || {}).filter(([_, v]) => num(v) > 0).map(([k, v]) => (
              <Field key={k} label={k.replace(/_/g, " ")} value={`₹ ${fmtINR(v)}`}/>
            ))}
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A] mt-4 mb-2">Yearly</div>
            {Object.entries(p.investments_yearly || {}).filter(([_, v]) => num(v) > 0).map(([k, v]) => (
              <Field key={k} label={k.replace(/_/g, " ")} value={`₹ ${fmtINR(v)}`}/>
            ))}
            <Field label="Monthly total" value={`₹ ${fmtINR(summary.monthly_sip)}`}/>
            <Field label="Yearly total" value={`₹ ${fmtINR(summary.yearly_invest)}`}/>
          </div>

          <div className="rounded-2xl border border-[#E5E2DA] bg-white p-7">
            <div className="font-heading text-xl mb-4">Insurance</div>
            <Field label="Term insurance" value={p.insurance?.has_term_insurance ? `Yes · ₹ ${fmtINR(p.insurance?.term_cover_amount)}` : "No"}/>
            <Field label="Term premium" value={`₹ ${fmtINR(p.insurance?.term_premium)}`}/>
            <Field label="Health insurance" value={p.insurance?.has_health_insurance ? `Yes · ₹ ${fmtINR(p.insurance?.health_cover_amount)}` : "No"}/>
            <Field label="Health premium" value={`₹ ${fmtINR(p.insurance?.health_premium)}`}/>
            <Field label="Employer health" value={p.insurance?.has_employer_health ? `Yes · ₹ ${fmtINR(p.insurance?.employer_health_cover)}` : "No"}/>
            <Field label="Suggested term cover" value={`₹ ${fmtINR(summary.term_target)}`}/>
            <Field label="Term gap" value={`₹ ${fmtINR(summary.term_gap)}`}/>
          </div>

          {(p.goals || []).length > 0 && (
            <div className="md:col-span-2 rounded-2xl border border-[#E5E2DA] bg-white p-7">
              <div className="font-heading text-xl mb-4">Goals</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {(p.goals || []).map((g, i) => (
                  <div key={i} className="rounded-xl border border-[#E5E2DA] p-4 bg-[#FDFBF7]">
                    <div className="text-sm font-medium">{g.name}</div>
                    <div className="mono text-lg text-[#1A3626] mt-1">₹ {fmtINR(g.today_cost)}</div>
                    <div className="text-xs text-[#8A8D8A]">Target {g.target_year || "—"} · {g.inflation || 7}% infl.</div>
                    {g.comment && <div className="text-xs text-[#5B5E5B] mt-2 whitespace-pre-wrap">{g.comment}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {p.financial_notes && (
            <div className="md:col-span-2 rounded-2xl border border-[#E5E2DA] bg-white p-7">
              <div className="font-heading text-xl mb-3">Notes from client</div>
              <div className="text-[#5B5E5B] whitespace-pre-wrap">{p.financial_notes}</div>
            </div>
          )}

          {Object.values(p.expenses || {}).some((arr) => (arr || []).length > 0) && (
            <div className="md:col-span-2 rounded-2xl border border-[#E5E2DA] bg-white p-7">
              <div className="font-heading text-xl mb-4">Expense detail</div>
              <Field label="Monthly total" value={`₹ ${fmtINR(summary.monthly_expense)}`}/>
              <Field label="Annual total" value={`₹ ${fmtINR(summary.annual_expense)}`}/>
              <div className="mt-4 grid md:grid-cols-2 gap-4">
                {Object.entries(p.expenses || {}).map(([group, items]) => (
                  (items || []).length > 0 && (
                    <div key={group} className="rounded-xl border border-[#E5E2DA] p-4 bg-[#FDFBF7]">
                      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A] mb-2">{group.replace(/_/g, " & ")}</div>
                      {items.map((it, i) => (
                        <div key={i} className="flex justify-between text-sm py-1">
                          <span className="text-[#5B5E5B]">{it.name || "—"} <span className="text-[#8A8D8A]">({it.frequency || "Monthly"})</span></span>
                          <span className="mono">₹ {fmtINR(it.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
